import React, { useState, useEffect } from 'react';
import { IcPlus, IcPencil, IcTrash, IcSearch, IcClock, IcConciergeBell, IcCpu, IcFlower, IcGem, IcCheckCircle, IcXCircle, IcClose } from '../../theme/icons';
import Swal from '../../theme/swal';
import serviceManagementService from '../../services/serviceManagementService';
import authService from '../../services/authService';
import ServiceDetailModal from './ServiceDetailModal';
import ServiceForm from './ServiceForm';
import SectionCard from '../../components/SectionCard';
import KI from '../../theme/palette';
import { TableSkeleton } from '../../components/Skeleton';
import { matchesSearch } from '../../utils/search';

// Type de service -> French label, pill colors and icon (mirrors ServiceForm).
const TYPE_META = {
  DIAGNOSTIC: { label: 'Diagnostique', className: 'bdg-type-diag', Icon: IcCpu },
  SOIN: { label: 'Soin', className: 'bdg-type-soin', Icon: IcFlower },
  ACTE: { label: 'Acte', className: 'bdg-type-acte', Icon: IcGem },
};

// Human-readable minutes -> "15 min" / "2 h" / "1 h 30 min".
const formatDuration = (mins) => {
  const m = parseInt(mins, 10);
  if (!m || m <= 0) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} h ${r} min`;
  if (h) return `${h} h`;
  return `${m} min`;
};

const formatPrice = (value) =>
  typeof value === 'number' ? value.toFixed(3) : (parseFloat(value) || 0).toFixed(3);

// `embedded` : rendu dans Paramètres › Services (entête compact, sans chip).
const ServicesList = ({ embedded = false } = {}) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [quickStatus, setQuickStatus] = useState('all'); // 'all' | 'active' | 'inactive'
  const [selectedService, setSelectedService] = useState(null);
  // Panneau Nouveau / Modifier (owner 2026-09-08) : 'new' | id | null — plus
  // de navigation vers /services/add ; la liste se recharge à la fermeture.
  const [serviceForm, setServiceForm] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const closeForm = (changed) => { setServiceForm(null); if (changed) setReloadTick((n) => n + 1); };

  // Check if user is admin
  const isAdmin = authService.hasRole(['admin']);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await serviceManagementService.getAllServices();
        const servicesList = Array.isArray(response?.results) ? response.results : (Array.isArray(response) ? response : []);
        setServices(servicesList);
        setError(null);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Erreur lors du chargement des services');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [reloadTick]);

  // Handle service deletion
  const handleDeleteService = async (serviceId) => {
    const result = await Swal.fire({
      title: 'Êtes-vous sûr?',
      text: 'Cette action ne peut pas être annulée!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.faint,
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await serviceManagementService.deleteService(serviceId);
        setServices((prev) => prev.filter(service => service.id !== serviceId));
        setSelectedService(null);
        Swal.fire('Supprimé!', 'Le service a été supprimé avec succès.', 'success');
      } catch (error) {
        console.error('Error deleting service:', error);

        // Get the specific error message from the response if available
        let errorMessage = 'Une erreur est survenue lors de la suppression.';
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }

        // If the error is related to sessions, offer the option to deactivate instead
        const isSessionRelatedError = errorMessage.includes('related sessions');
        if (isSessionRelatedError) {
          const confirmDeactivate = await Swal.fire({
            title: 'Service utilisé!',
            text: errorMessage + '\nVoulez-vous désactiver ce service à la place?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: KI.peacock,
            cancelButtonColor: KI.faint,
            confirmButtonText: 'Oui, désactiver',
            cancelButtonText: 'Annuler'
          });

          if (confirmDeactivate.isConfirmed) {
            await handleToggleStatus(serviceId, true);
          }
        } else {
          Swal.fire('Erreur!', errorMessage, 'error');
        }
      }
    }
  };

  // Toggle service status. `forceInactive` is used by the delete fallback.
  const handleToggleStatus = async (serviceId, forceInactive = false) => {
    try {
      const serviceToUpdate = services.find(service => service.id === serviceId);
      if (!serviceToUpdate) return;

      const newActive = forceInactive ? false : !serviceToUpdate.is_active;
      const updatedServiceData = {
        id: serviceId,
        name: serviceToUpdate.name,
        type: serviceToUpdate.type || 'SOIN',
        price: serviceToUpdate.price,
        remise: serviceToUpdate.remise || 0,
        description: serviceToUpdate.description,
        is_active: newActive,
      };

      await serviceManagementService.updateService(serviceId, updatedServiceData);

      setServices((prev) => prev.map(service =>
        service.id === serviceId ? { ...service, is_active: newActive } : service
      ));
      // Keep the open detail modal (if any) in sync.
      setSelectedService((prev) =>
        prev && prev.id === serviceId ? { ...prev, is_active: newActive } : prev
      );

      Swal.fire(
        forceInactive ? 'Désactivé!' : 'Mis à jour!',
        forceInactive ? 'Le service a été désactivé avec succès.' : 'Le statut du service a été modifié.',
        'success'
      );
    } catch (error) {
      console.error('Error updating service status:', error);
      Swal.fire('Erreur!', 'Une erreur est survenue lors de la mise à jour.', 'error');
    }
  };

  // Counts for the stat cards
  const activeCount = services.filter((s) => s.is_active).length;
  const inactiveCount = services.length - activeCount;

  // Client-side derived list (search + quick status)
  const displayedServices = services.filter((service) => {
    if (quickStatus === 'active' && !service.is_active) return false;
    if (quickStatus === 'inactive' && service.is_active) return false;
    // Règle Ki Para (utils/search.js) : début de mot, sans accents.
    return matchesSearch(searchTerm, [service.name, service.description, TYPE_META[service.type]?.label]);
  });

  // Clickable stat card (quick status filter)
  // Carte KPI (Composants-2 §5) = filtre rapide : blanche, bordée + drapeau
  // quand le filtre est actif (même motif que /clients et /facturation).
  const StatCard = ({ title, count, statusKey, Icon, valueClass = '', sub }) => {
    const active = quickStatus === statusKey;
    return (
      <button
        type="button"
        onClick={() => setQuickStatus(active && statusKey !== 'all' ? 'all' : statusKey)}
        className={`text-left kpi rounded-2xl p-5 transition-all ${active && statusKey !== 'all' ? 'kpi-active' : 'hover:shadow-md'}`}
      >
        {active && statusKey !== 'all' && <span className="kpi-flag">FILTRE ACTIF</span>}
        <Icon size={16} className="kpi-icon" />
        <p className="kpi-label text-sm uppercase pr-6">{title}</p>
        <p className={`kpi-value text-2xl ${valueClass}`}>{count}</p>
        <p className="kpi-sub text-xs mt-1">{active && statusKey !== 'all' ? 'Filtre actif · cliquer pour tout voir' : sub}</p>
      </button>
    );
  };

  const listContent = loading ? (
        <TableSkeleton rows={6} columns={[
              { label: 'Service', w: 220 },
              { label: 'Type', w: 80 },
              { label: 'Durée', w: 80 },
              { label: 'Prix', align: 'right', w: 72 },
              { label: 'Statut', w: 80 },
              { label: 'Actions', align: 'right', w: 60 },
            ]} />
      ) : error ? (
        <div className={`bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl ${embedded ? 'm-6' : ''}`} role="alert">
          {error}
        </div>
      ) : displayedServices.length === 0 ? (
        /* État vide (Composants-2 §9) */
        <div className={`empty-ki rounded-2xl p-12 ${embedded ? 'm-6' : ''}`}>
          <IcConciergeBell size={48} className="empty-ki-icon w-12 h-12 mb-4" />
          <p className="empty-ki-title text-lg mt-0">
            {searchTerm || quickStatus !== 'all' ? 'Aucun service correspondant' : 'Aucun service'}
          </p>
          <p className="empty-ki-sub text-sm mt-1 mb-0 max-w-md mx-auto">
            {searchTerm || quickStatus !== 'all' ? 'Essayez un autre mot, ou retirez le filtre.' : 'Pour commencer, créez votre premier service.'}
          </p>
          {!searchTerm && quickStatus === 'all' && isAdmin && (
            <button type="button" onClick={() => setServiceForm('new')} className="btn-add-block w-auto mt-4 px-4 py-2.5">
              <IcPlus className="mr-2" /> Ajouter un service
            </button>
          )}
        </div>
      ) : (
        /* Liste (owner 2026-09-09 : cartes -> tableau dense, Composants-2 §6) :
           entête th-ki, ligne = fiche au clic, actions à droite. */
        <div className={embedded ? '' : 'bg-white rounded-2xl border border-gray-200 overflow-hidden'}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase">Service</th>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-36">Type</th>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-28">Durée</th>
                  <th scope="col" className="th-ki px-5 py-3 text-right uppercase w-40">Prix</th>
                  <th scope="col" className="th-ki px-5 py-3 text-left uppercase w-28">Statut</th>
                  {isAdmin && <th scope="col" className="th-ki px-5 py-3 text-right uppercase w-52">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {displayedServices.map((service) => {
                  const type = TYPE_META[service.type];
                  const TypeIcon = type ? type.Icon : IcConciergeBell;
                  const hasRemise = service.remise > 0;
                  const finalPrice = service.final_price != null
                    ? service.final_price
                    : service.price * (1 - (service.remise || 0) / 100);
                  const duration = formatDuration(service.duration);
                  return (
                    <tr
                      key={service.id}
                      onClick={() => setSelectedService(service)}
                      className="border-t border-gray-100 tr-ki transition-colors"
                    >
                      <td className="td-ki px-5 py-3 text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="h-9 w-9 shrink-0 rounded-lg overflow-hidden bg-gray-100 text-gray-700 flex items-center justify-center">
                            {service.image ? (
                              <img src={service.image} alt={service.name} className="h-full w-full object-cover" />
                            ) : (
                              <TypeIcon size={16} />
                            )}
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 leading-tight truncate">{service.name}</p>
                            {service.description && (
                              <p className="text-xs text-gray-400 mt-0.5 max-w-md truncate" title={service.description}>{service.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap">
                        {type ? <span className={`bdg ${type.className}`}>{type.label}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-gray-500 tabular-nums">
                        {duration ? <span className="inline-flex items-center gap-1.5"><IcClock size={12} /> {duration}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-right tabular-nums">
                        <span className="font-semibold text-gray-900">{formatPrice(finalPrice)} DT</span>
                        {hasRemise && (
                          <div className="flex items-center justify-end gap-2 mt-0.5">
                            <span className="text-xs text-gray-400 line-through">{formatPrice(service.price)} DT</span>
                            <span className="bdg bdg-promo">−{service.remise}%</span>
                          </div>
                        )}
                      </td>
                      <td className="td-ki px-5 py-3 text-sm whitespace-nowrap">
                        <span className={`bdg ${service.is_active ? 'bdg-pos' : 'bdg-neutral'}`}>
                          {service.is_active ? '● Actif' : '○ Inactif'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="td-ki px-5 py-3 text-sm whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(service.id)}
                              className={service.is_active
                                ? 'inline-flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-iface-600 hover:text-gray-900'
                                : 'text-xs font-medium px-3 py-1.5 rounded-lg transition-colors text-green-700 hover:bg-green-50'}
                            >
                              {service.is_active ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setServiceForm(service.id)}
                              title="Modifier"
                              aria-label="Modifier le service"
                              className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors"
                            >
                              <IcPencil />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteService(service.id)}
                              title="Supprimer"
                              aria-label="Supprimer le service"
                              className="h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <IcTrash />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );

  return (
    <div className={embedded ? 'w-full' : 'w-full px-4 pt-2 pb-4'}>
      {/* Paramètres › Services (Studio owner 2026-09-09) : ni titre, ni recherche,
          ni cartes KPI — seulement les cartes, « Ajouter un service » dessous à droite.
          La page /services seule garde son en-tête, sa recherche et ses KPI. */}
      {!embedded && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
        <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm self-start">
          <IcConciergeBell size={13} /> Liste des services
        </button>
        {isAdmin && (
          <button type="button" onClick={() => setServiceForm('new')} className="btn-add-block w-auto px-4 py-2.5 transition-colors self-start sm:self-auto">
            <IcPlus className="mr-2" />
            Ajouter un service
          </button>
        )}
      </div>
      )}

      {/* Recherche pleine largeur — même toolbar que /clients et /products. */}
      {!embedded && (
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher par nom, description ou type..."
          autoComplete="off"
          className="inp h-12 w-full pl-11 pr-10"
        />
        {searchTerm && (
          <button type="button" onClick={() => setSearchTerm('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Effacer la recherche">
            <IcClose />
          </button>
        )}
      </div>
      )}

      {/* Cartes KPI = filtres rapides (page seule) */}
      {!embedded && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard title="Total services" count={services.length} statusKey="all" Icon={IcConciergeBell} sub="Tous les services" />
        <StatCard title="Services actifs" count={activeCount} statusKey="active" Icon={IcCheckCircle} valueClass="text-green-900" sub="Cliquer pour filtrer" />
        <StatCard title="Services inactifs" count={inactiveCount} statusKey="inactive" Icon={IcXCircle} valueClass="text-red-700" sub="Cliquer pour filtrer" />
      </div>
      )}

      {/* Paramètres : même cadre que Tarification (owner 2026-09-09) — carte de
          section titre + compte, tableau bord à bord, « Ajouter un service » en pied. */}
      {embedded ? (
        <SectionCard
          title="Services"
          sub={`${services.length} service(s) · ${activeCount} actif(s)`}
          flush
          action={isAdmin && !loading && (
            <button
              type="button"
              onClick={() => setServiceForm('new')}
              className="h-9 w-9 flex items-center justify-center btn-mini transition-colors shrink-0"
              title="Ajouter un service"
              aria-label="Ajouter un service"
            >
              <IcPlus />
            </button>
          )}
        >
          {listContent}
        </SectionCard>
      ) : listContent}

      {/* Fiche service */}
      {selectedService && (
        <ServiceDetailModal
          service={selectedService}
          isAdmin={isAdmin}
          onClose={() => setSelectedService(null)}
          onToggleStatus={handleToggleStatus}
          onDelete={handleDeleteService}
          onEdit={(svc) => { setSelectedService(null); setServiceForm(svc.id); }}
        />
      )}

      {/* Panneau Nouveau / Modifier service — même patron de modale que les
          catégories (entête encre, corps = ServiceForm en mode panneau). */}
      {serviceForm !== null && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) closeForm(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">
                {serviceForm === 'new' ? 'Nouveau service' : 'Modifier le service'}
              </h3>
              <button type="button" onClick={() => closeForm(false)} className="text-gray-400 hover:text-white" aria-label="Fermer">
                <IcClose size={18} />
              </button>
            </div>
            <ServiceForm
              key={serviceForm}
              id={serviceForm === 'new' ? undefined : serviceForm}
              onDone={() => closeForm(true)}
              onCancel={() => closeForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesList;
